import {Image} from '@tiptap/extension-image'
import type {Node as PMNode} from '@tiptap/pm/model'
import {NodeSelection} from '@tiptap/pm/state'

const MIN_WIDTH = 40

const HANDLES = [
  {name: 'nw', factor: -1},
  {name: 'ne', factor: 1},
  {name: 'sw', factor: -1},
  {name: 'se', factor: 1},
] as const

/**
 * Images keep explicit dimensions (legacy content resized via Quill's blot
 * formatter stores width/height attributes) and stay responsive by default.
 *
 * The node view adds corner drag handles when the image is selected: dragging
 * commits a `width` attribute in px and clears any stored `height`, so the
 * browser preserves the aspect ratio wherever the HTML is rendered — no
 * editor CSS needed, matching how the old Quill blot formatter serialized
 * resizes.
 */
export const ResizableImage = Image.configure({inline: false, allowBase64: true}).extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {default: null},
      height: {default: null},
      style: {default: null},
    }
  },

  addNodeView() {
    return ({node, editor, getPos}) => {
      let currentNode = node

      const wrap = document.createElement('div')
      wrap.className = 'rte-image-resizer'

      const img = document.createElement('img')
      wrap.appendChild(img)

      const applyAttrs = (n: PMNode) => {
        for (const attr of ['src', 'alt', 'title', 'width', 'height', 'style'] as const) {
          const value = n.attrs[attr]
          if (value === null || value === undefined || value === '') img.removeAttribute(attr)
          else img.setAttribute(attr, String(value))
        }
      }
      applyAttrs(node)

      const startResize = (event: PointerEvent, factor: number) => {
        if (!editor.isEditable) return
        event.preventDefault()
        event.stopPropagation()

        const pos = getPos()
        if (typeof pos !== 'number') return

        // Select the node so the handles/outline stay visible while dragging
        // (stopPropagation keeps ProseMirror from doing it for us).
        const {state} = editor.view
        editor.view.dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)))

        // jsdom (and images not yet laid out) report a 0-width rect; fall back
        // to the stored attribute so resizing still has a starting point.
        const startWidth = img.getBoundingClientRect().width || Number(currentNode.attrs.width) || 0
        if (!startWidth) return

        const surface = wrap.closest<HTMLElement>('.tiptap')
        const maxWidth = surface?.clientWidth || Infinity
        const startX = event.clientX
        wrap.classList.add('rte-resizing')

        const onMove = (e: PointerEvent) => {
          const width = Math.round(
            Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + factor * (e.clientX - startX)))
          )
          img.style.width = `${width}px`
        }

        const onUp = (e: PointerEvent) => {
          document.removeEventListener('pointermove', onMove)
          document.removeEventListener('pointerup', onUp)
          wrap.classList.remove('rte-resizing')
          img.style.width = ''

          const width = Math.round(
            Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + factor * (e.clientX - startX)))
          )
          const commitPos = getPos()
          if (typeof commitPos !== 'number') return
          const tr = editor.view.state.tr.setNodeMarkup(commitPos, undefined, {
            ...currentNode.attrs,
            width,
            height: null,
          })
          editor.view.dispatch(tr)
        }

        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
      }

      for (const {name, factor} of HANDLES) {
        const handle = document.createElement('span')
        handle.className = `rte-resize-handle rte-handle-${name}`
        handle.setAttribute('aria-hidden', 'true')
        handle.addEventListener('pointerdown', (e) => startResize(e, factor))
        wrap.appendChild(handle)
      }

      return {
        dom: wrap,
        update(updated) {
          if (updated.type !== currentNode.type) return false
          currentNode = updated
          applyAttrs(updated)
          return true
        },
        stopEvent(event) {
          return (
            (event.target as HTMLElement | null)?.classList?.contains('rte-resize-handle') ?? false
          )
        },
        ignoreMutation() {
          return true
        },
      }
    }
  },
})
